'use client'

import { CheckboxIcon } from '@/app/[locale]/_assets/icon-components'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import { useDynamicLanguage } from '@/app/[locale]/_hooks/useShipper'
import { getTags } from '@/app/[locale]/_service/tags.service'
import { IRedux } from '@/app/[locale]/_store/types'
import { Avatar } from '@/components/ui/avatar'
import { CheckboxCard } from '@/components/ui/checkbox-card'
import {
  Flex,
  Text,
  Center,
  SimpleGrid,
  CheckboxGroup,
  useBreakpointValue,
  Stack,
  HStack,
  Box,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import React from 'react'
import { useSelector } from 'react-redux'

interface IRadioGroup {
  values: string[]
  onChange: (value: string[]) => void
}
const Tags = ({ values, onChange }: IRadioGroup) => {
  const t = useTranslations()
  const currentLocale = useCustomLocale()
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )
  const session = useSelector((state: IRedux) => state.session)
  const settings = useSelector((state: IRedux) => state.settings)

  const { useDynamicLanguage: dynamicLanguageEnabled, isShipperResolved } =
    useDynamicLanguage()

  const { data: tags } = useQuery({
    queryKey: ['get_tag_list', dynamicLanguageEnabled],
    queryFn: () => getTags({ page: 1, limit: 10 }, dynamicLanguageEnabled),
    enabled:
      isShipperResolved &&
      Boolean(session?.shipper_id || settings?.data?.shipper_id),
  })

  const handleChange = (selectedValues: string[]) => {
    onChange(selectedValues)
  }

  const handleToggle = (itemId: string) => {
    const isSelected = values.includes(itemId)
    const newValues = isSelected
      ? values.filter((id) => id !== itemId)
      : [...values, itemId]

    onChange(newValues)
  }

  if (!tags?.data?.tags) return null

  return (
    <>
      <Flex alignItems='center' justifyContent='space-between' mt={4} mb={2}>
        <Text fontWeight={600} fontSize={'lg'} lineHeight={'28px'}>
          {t('special')}
        </Text>
      </Flex>
      <CheckboxGroup value={values} onValueChange={handleChange} name='tags'>
        {isDesktop ? (
          <Stack pr={3}>
            {tags?.data?.tags?.map((item) => (
              <HStack
                py={1}
                key={item.id}
                justifyContent={'space-between'}
                fontSize={{ base: 'sm', lg: 'md' }}
                onClick={() => handleToggle(item.id)}
              >
                <label htmlFor={item.id}>
                  <HStack>
                    <Avatar
                      size='sm'
                      key={item.id}
                      name={item?.title?.[currentLocale]}
                      src={
                        item.icon
                          ? `${process.env.BASE_URL}${item.icon}`
                          : undefined
                      }
                    />
                    <p>{item.title?.[currentLocale]}</p>
                  </HStack>
                </label>
                <CheckboxCard
                  unstyled
                  icon={
                    values?.includes(item.id) ? (
                      <CheckboxIcon color={'var(--primary-default)'} />
                    ) : (
                      <Box bg={'light.300'} w={7} h={7} rounded={'full'} />
                    )
                  }
                  indicator={false}
                />
              </HStack>
            ))}
          </Stack>
        ) : (
          <SimpleGrid gap={5} columns={4} mt={4}>
            {tags?.data?.tags?.map((item) => (
              <Center
                flexDir={'column'}
                key={item.id}
                gap={2}
                onClick={() => handleToggle(item.id)}
              >
                <CheckboxCard
                  align='center'
                  size={'sm'}
                  flexDir={'row'}
                  unstyled
                  icon={
                    <Avatar
                      size='sm'
                      key={item.id}
                      name={item?.title?.[currentLocale]}
                      src={
                        item.icon
                          ? `${process.env.BASE_URL}${item.icon}`
                          : undefined
                      }
                    />
                  }
                  indicator={false}
                />
                <label htmlFor={item.id}>
                  <Text
                    lineClamp={1}
                    wordBreak='break-all'
                    rounded={12}
                    px={2}
                    mx={-2}
                    {...(values?.includes(item.id)
                      ? { bg: 'primary', color: 'primary.text' }
                      : {})}
                  >
                    {item.title?.[currentLocale]}
                  </Text>
                </label>
              </Center>
            ))}
          </SimpleGrid>
        )}
      </CheckboxGroup>
    </>
  )
}

export default Tags
